import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { whatsappOrderLink } from "../lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, wrapFee, total, gift } = useCart();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-ink/50"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%", rotateY: -18, opacity: 0.6 }}
            animate={{ x: 0, rotateY: 0, opacity: 1 }}
            exit={{ x: "100%", rotateY: -12, opacity: 0.6 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformPerspective: 1400, transformOrigin: "right center" }}
            className="fixed inset-y-0 right-0 z-[80] w-full max-w-md bg-ivory/95 backdrop-blur-md border-l border-gold/30 flex flex-col text-ink shadow-[-30px_0_80px_-30px_rgba(14,13,11,0.4)]"
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-ink/10">
              <h2 className="font-serif text-2xl tracking-wide">
                Your Cart <span className="text-gold-dark text-base align-middle">({items.length})</span>
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="p-2 text-ink/50 hover:text-gold-dark hover:rotate-90 transition-[color,transform] duration-300"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty-state" className="h-full flex flex-col items-center justify-center text-center gap-4 text-ink/40">
                  <motion.div animate={{ y: [0, -8, 0], rotateY: [0, 20, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
                    <ShoppingBag size={36} strokeWidth={1} />
                  </motion.div>
                  <p className="text-sm tracking-wide">Your cart is empty.</p>
                  <button
                    data-testid="cart-empty-shop-button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/shop");
                    }}
                    className="text-[10px] tracking-[0.3em] uppercase text-gold-dark border-b border-gold/40 pb-1 hover:text-gold"
                  >
                    Discover the collection
                  </button>
                </div>
              ) : (
                items.map((item, i) => (
                  <motion.div
                    key={item.key}
                    layout
                    data-testid={`cart-item-${item.id}`} data-variant={item.variant || ""}
                    initial={{ opacity: 0, x: 40, rotateY: 25 }}
                    animate={{ opacity: 1, x: 0, rotateY: 0 }}
                    exit={{ opacity: 0, x: 40 }}
                    transition={{ delay: 0.15 + i * 0.07, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    style={{ transformPerspective: 800 }}
                    className="flex gap-4"
                  >
                    <div className="w-20 h-24 shrink-0 overflow-hidden bg-sand shadow-md">
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[9px] tracking-[0.3em] uppercase text-ink/40">{item.category}</p>
                          <h3 className="font-serif text-lg leading-snug mt-0.5 truncate">{item.name}</h3>
                          {item.variant && (
                            <p data-testid={`cart-item-variant-${item.id}`} className="text-[10px] tracking-[0.2em] uppercase text-gold-dark mt-0.5">{item.variant}</p>
                          )}
                        </div>
                        <button
                          data-testid={`cart-remove-${item.id}`}
                          onClick={() => remove(item.key)}
                          className="p-1 text-ink/35 hover:text-red-500 transition-colors"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 size={15} strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center border border-ink/15">
                          <button
                            data-testid={`cart-qty-minus-${item.id}`}
                            onClick={() => setQty(item.key, item.qty - 1)}
                            className="px-2.5 py-1.5 text-ink/60 hover:text-gold-dark transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={13} />
                          </button>
                          <span data-testid={`cart-qty-${item.id}`} className="w-7 text-center text-sm">{item.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${item.id}`}
                            onClick={() => setQty(item.key, item.qty + 1)}
                            className="px-2.5 py-1.5 text-ink/60 hover:text-gold-dark transition-colors"
                            aria-label="Increase quantity"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                        <p className="text-gold-dark text-sm">{format(item.price * item.qty)}</p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-ink/10 px-7 py-6 space-y-4 bg-champagne/60">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-ink/50">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl text-gold-dark">
                    {format(subtotal)}
                  </span>
                </div>
                {wrapFee > 0 ? (
                  <div className="flex items-center justify-between text-xs text-ink/60">
                    <span>{gift.tier === "signature" ? "Gold Signature Wrapping" : "Classic Wrapping"}</span>
                    <span data-testid="cart-wrap-fee">{format(wrapFee)}</span>
                  </div>
                ) : (
                  <p className="text-[10px] tracking-[0.2em] uppercase text-ink/40">+ Gold Signature Wrapping available at checkout</p>
                )}
                {wrapFee > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-ink/50">Total</span>
                    <span data-testid="cart-total" className="font-serif text-xl">{format(total)}</span>
                  </div>
                )}
                <button
                  data-testid="cart-checkout-button"
                  onClick={goCheckout}
                  className="w-full bg-ink text-ivory py-4 text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-gold hover:text-ink transition-colors duration-300"
                >
                  Checkout
                </button>
                <a
                  data-testid="cart-whatsapp-button"
                  href={whatsappOrderLink(items, total, null, format, gift)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2.5 border border-gold/50 text-gold-dark py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-gold/10 transition-colors duration-300"
                >
                  <MessageCircle size={15} strokeWidth={1.5} />
                  Order via WhatsApp
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
